/**
 * Overview banners for Sage changes FPA has not absorbed yet: money posted to accounts nobody
 * classified (never counted as OpEx until an admin classifies them) and Sage departments no tile
 * groups (shown as auto tiles). Links go to the settings pages for the roles that can act.
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import type { Runway } from '@/api/types'
import { Notice } from '@/components/ui/Notice'
import { useMe } from '@/features/auth/MeContext'
import { formatMoney } from '@/lib/format'

import { useCarriedSearch } from '../hooks/usePeriod'
import styles from './ConfigNotices.module.css'

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

export function ConfigNotices({ runway }: { runway: Runway }) {
  const me = useMe()
  const search = useCarriedSearch()
  const { unclassified } = runway
  const autoNames = useMemo(
    () =>
      runway.department_order
        // Sage parents with sub-departments roll up on their own (ADR-0005): only lone Sage
        // departments no tile groups are worth a look.
        .filter((code) => runway.departments[code]?.auto && runway.departments[code]?.grouping === 'sage')
        .map((code) => runway.departments[code]?.name ?? code),
    [runway.department_order, runway.departments],
  )
  const accountsLink = useMemo(() => ({ pathname: '/settings/accounts', search }), [search])
  const departmentsLink = useMemo(() => ({ pathname: '/settings/departments', search }), [search])
  const outsideLink = useMemo(() => ({ pathname: '/settings/departments', search, hash: '#outside' }), [search])
  const outside = runway.outside_tiles

  return (
    <>
      {outside.count > 0 ? (
        <Notice tone="info">
          <b>
            {formatMoney(outside.actual)} in {outside.count} {plural(outside.count, 'department', 'departments')}{' '}
            outside the dashboard
          </b>{' '}
          — Sage departments no tile groups ({outside.tiles.map((t) => t.name).join(', ')}). They are not in the totals
          below, which add up the departments shown.{' '}
          {me.permissions.canConfigure ? (
            <Link to={outsideLink} className={styles.link}>
              See them in settings
            </Link>
          ) : (
            <>Ask an FPA admin or manager to group or show them if needed.</>
          )}
        </Notice>
      ) : null}
      {unclassified.count > 0 ? (
        <Notice tone="warn">
          <b>
            {formatMoney(unclassified.amount)} in {unclassified.count} new Sage{' '}
            {plural(unclassified.count, 'account', 'accounts')} isn&rsquo;t counted
          </b>{' '}
          — {plural(unclassified.count, 'it is', 'they are')} not classified as operating expense or excluded yet (
          {unclassified.accounts.map((a) => `${a.acct} ${a.title}`).join(', ')}).{' '}
          {me.permissions.canConfigureAccounting ? (
            <Link to={accountsLink} className={styles.link}>
              Classify them
            </Link>
          ) : (
            <>Ask an FPA admin to classify them.</>
          )}
        </Notice>
      ) : null}
      {autoNames.length > 0 ? (
        <Notice tone="info">
          <b>
            {autoNames.length} {plural(autoNames.length, 'department', 'departments')} not grouped
          </b>{' '}
          — Sage has {plural(autoNames.length, 'a department', 'departments')} no tile covers ({autoNames.join(', ')}
          ), shown as {plural(autoNames.length, 'its own tile', 'their own tiles')}. Nothing is lost: the totals include{' '}
          {plural(autoNames.length, 'it', 'them')}.{' '}
          {me.permissions.canConfigure ? (
            <Link to={departmentsLink} className={styles.link}>
              Group or rename in settings
            </Link>
          ) : (
            <>Ask an FPA admin or manager to group {plural(autoNames.length, 'it', 'them')} if needed.</>
          )}
        </Notice>
      ) : null}
    </>
  )
}
